import { useMemo } from "react";
import { AXIS_HEIGHT } from "./constants";
import type { TimelineScales } from "../../hooks/useTimelineScales";

interface XAxisProps {
  scales: TimelineScales;
  height: number;
}

export function XAxis({ scales, height }: XAxisProps) {
  const ticks = useMemo(() => {
    const out: { x: number; year: number; isDecade: boolean; isHalfDecade: boolean }[] = [];
    const startYear = scales.startDate.getUTCFullYear();
    const endYear = scales.endDate.getUTCFullYear();
    for (let y = startYear; y <= endYear; y++) {
      const x = scales.xScale(new Date(Date.UTC(y, 0, 1)));
      out.push({
        x,
        year: y,
        isDecade: y % 10 === 0,
        isHalfDecade: y % 5 === 0 && y % 10 !== 0,
      });
    }
    return out;
  }, [scales]);

  const nowX = scales.xScale(new Date());

  return (
    <g className="x-axis">
      {/* Vertical grid — hairline rules at every year, slightly bolder at half-decades, prominent at decades */}
      {ticks.map((t) => {
        let stroke = "var(--color-rule-soft)";
        let strokeWidth = 0.5;
        if (t.isDecade) {
          stroke = "var(--color-rule)";
          strokeWidth = 1;
        } else if (t.isHalfDecade) {
          stroke = "var(--color-rule-soft)";
          strokeWidth = 0.8;
        }
        return (
          <line
            key={t.year}
            x1={t.x}
            x2={t.x}
            y1={AXIS_HEIGHT - 2}
            y2={height}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeDasharray={t.isDecade ? undefined : "1 3"}
            opacity={t.isDecade ? 0.7 : 0.5}
          />
        );
      })}

      {/* "Now" guide — copper hairline, full height */}
      <line
        x1={nowX}
        x2={nowX}
        y1={AXIS_HEIGHT - 2}
        y2={height}
        stroke="var(--color-now)"
        strokeWidth={1}
        strokeDasharray="4 5"
        opacity={0.55}
      />

      {/* Horizontal axis baseline */}
      <line
        x1={0}
        x2={scales.xScale(scales.endDate) + 200}
        y1={AXIS_HEIGHT - 1}
        y2={AXIS_HEIGHT - 1}
        stroke="var(--color-rule)"
        strokeWidth={1}
      />

      {/* Year labels — serif italic for decades, monospace tabular for half-decades */}
      {ticks.map((t) => {
        if (t.isDecade) {
          return (
            <g key={`label-${t.year}`}>
              <text
                x={t.x}
                y={AXIS_HEIGHT - 18}
                fill="var(--color-cream)"
                fontSize={22}
                fontFamily="var(--font-display)"
                fontStyle="italic"
                fontWeight={400}
                letterSpacing="-0.02em"
                textAnchor="middle"
                className="select-none"
              >
                {t.year}
              </text>
            </g>
          );
        }
        if (!t.isHalfDecade) return null;
        return (
          <text
            key={`label-${t.year}`}
            x={t.x}
            y={AXIS_HEIGHT - 12}
            fill="var(--color-cream-dim)"
            fontSize={9}
            fontFamily="var(--font-mono)"
            fontWeight={400}
            letterSpacing="0.05em"
            textAnchor="middle"
            className="select-none"
          >
            '{String(t.year).slice(2)}
          </text>
        );
      })}

      {/* Now marker label */}
      <g>
        <line
          x1={nowX}
          x2={nowX}
          y1={AXIS_HEIGHT - 12}
          y2={AXIS_HEIGHT - 2}
          stroke="var(--color-now)"
          strokeWidth={1.5}
        />
        <text
          x={nowX + 6}
          y={AXIS_HEIGHT - 5}
          fill="var(--color-now)"
          fontSize={9}
          fontFamily="var(--font-mono)"
          fontWeight={500}
          letterSpacing="0.18em"
          className="select-none"
        >
          NOW
        </text>
      </g>
    </g>
  );
}
